/**
 * Shared primitive Zod schemas for deal-domain objects.
 * All BigDecimal values travel as strings — never coerce to number.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// BigDecimal string — matches Java BigDecimal wire format from Jackson.
// Regex: optional leading minus, digits, optional decimal point + digits.
// ---------------------------------------------------------------------------
export const BigDecimalString = z
  .string()
  .min(1, 'Required')
  .regex(/^-?\d+(\.\d+)?$/, 'Must be a valid decimal number');

/** BigDecimal string, optional (allows empty string to represent absence). */
export const OptionalBigDecimalString = z
  .string()
  .regex(/^(-?\d+(\.\d+)?)?$/, 'Must be a valid decimal number or empty')
  .optional();

// ---------------------------------------------------------------------------
// ISO 8601 date string (YYYY-MM-DD).
// ---------------------------------------------------------------------------
export const IsoDateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be a date in YYYY-MM-DD format');

export const OptionalIsoDateString = IsoDateString.optional();

// ---------------------------------------------------------------------------
// ISO 4217 currency code — 3 uppercase letters.
// ---------------------------------------------------------------------------
export const CurrencyCode = z
  .string()
  .length(3, 'Must be a 3-letter ISO 4217 code')
  .regex(/^[A-Z]{3}$/, 'Must be uppercase letters only');

export const OptionalCurrencyCode = CurrencyCode.optional();

// ---------------------------------------------------------------------------
// Counterparty reference — a non-empty string ID.
// ---------------------------------------------------------------------------
export const PartyRef = z.object({
  id: z.string().min(1, 'Required'),
  name: z.string().optional(),
});

export type PartyRef = z.infer<typeof PartyRef>;

export const CounterpartyRef = PartyRef;
export type CounterpartyRef = PartyRef;

// ---------------------------------------------------------------------------
// Portfolio reference.
// ---------------------------------------------------------------------------
export const PortfolioRef = z.object({
  id: z.string().min(1, 'Required'),
  name: z.string().optional(),
});

export type PortfolioRef = z.infer<typeof PortfolioRef>;

// ---------------------------------------------------------------------------
// Leg primitives — reused across IR, FX, and credit products.
// ---------------------------------------------------------------------------

export const LegSide = z.enum(['BUY', 'SELL', 'PAY', 'RECEIVE']);
export type LegSide = z.infer<typeof LegSide>;

export const BuySell = z.enum(['BUY', 'SELL']);
export type BuySell = z.infer<typeof BuySell>;

export const DayCountConvention = z.enum([
  'ACT_360',
  'ACT_365',
  'ACT_ACT',
  '30_360',
  '30E_360',
]);
export type DayCountConvention = z.infer<typeof DayCountConvention>;

export const Frequency = z.enum([
  'ANNUAL',
  'SEMI_ANNUAL',
  'QUARTERLY',
  'MONTHLY',
  'ZERO',
]);
export type Frequency = z.infer<typeof Frequency>;

/** A single cash-flow leg primitive. */
export const LegPrimitive = z.object({
  side: LegSide,
  notional: BigDecimalString,
  currency: CurrencyCode,
  rate: OptionalBigDecimalString,
  startDate: IsoDateString,
  endDate: IsoDateString,
  dayCount: DayCountConvention.optional(),
  frequency: Frequency.optional(),
});

export type LegPrimitive = z.infer<typeof LegPrimitive>;
